import PaiButton from 'features/ui/base-components/pai-button';
import PaiRadioGroup from 'features/ui/base-components/pai-radio-group';
import { Stack } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserData } from 'hooks/use-user-data';
import CampaignMessageChronicle from './campaign-message-chronicle';
import { LinkedinContact } from './campaign-statistics-types';
import { exportCampaignMessageHistoryToExcel } from './message-history-export';

export interface MessageHistoryPanelProps {
  messageHistory: LinkedinContact[];
  timeZone: string | undefined;
  contactDrawerLoading: boolean;
  handleOpenContactDrawer(linkedInURL: string, profileId?: string, funnelId?: string): () => void;
  /** Campaign IDs used to fetch enriched rows for Excel export */
  funnelIdsForExport: string[];
  /** When false, only sentiment filters + chronicle (export lives elsewhere, e.g. statistics accordion header). */
  showExportButton?: boolean;
}

/**
 * Shared message history: sentiment filters, optional export, and chronicle.
 * Used from campaign overview drawer and from statistics dashboard.
 */
const MessageHistoryPanel: React.FC<MessageHistoryPanelProps> = ({
  messageHistory = [],
  timeZone,
  contactDrawerLoading,
  handleOpenContactDrawer,
  funnelIdsForExport,
  showExportButton = true,
}) => {
  const { t } = useTranslation();
  const { token } = useUserData();
  const [sentimentFilter, setSentimentFilter] = useState('all');

  const processedMessageHistory = useMemo(
    () =>
      messageHistory.map((contact) => {
        const hasReceived = contact.messages?.some((msg) => msg.direction === 'received');
        return {
          ...contact,
          sentiment: hasReceived ? contact.sentiment : undefined,
        };
      }),
    [messageHistory],
  );

  const handleSentimentFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    setSentimentFilter(event.target.value);
  };

  const exportToExcel = async () => {
    await exportCampaignMessageHistoryToExcel(funnelIdsForExport, token);
  };

  const filteredContacts = processedMessageHistory.filter((contact) =>
    sentimentFilter === 'all'
      ? true
      : sentimentFilter === 'positive'
        ? contact.sentiment?.includes('positive')
        : contact.sentiment?.includes('negative'),
  );

  return (
    <Stack gap={2}>
      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <PaiRadioGroup
          row={true}
          size={'small'}
          items={[
            { label: t('all'), value: 'all' },
            {
              label: t('positiveRepliesCount', {
                count: processedMessageHistory.filter((c) => c.sentiment?.includes('positive')).length,
              }),
              value: 'positive',
            },
            {
              label: t('negativeRepliesCount', {
                count: processedMessageHistory.filter((c) => c.sentiment?.includes('negative')).length,
              }),
              value: 'negative',
            },
          ]}
          value={sentimentFilter}
          onChange={handleSentimentFilterChange}
        />
        {showExportButton ? (
          <PaiButton
            text={t('csvExport')}
            size={'small'}
            border={'round'}
            action={exportToExcel}
            disabled={processedMessageHistory.length === 0}
          />
        ) : null}
      </Stack>
      <CampaignMessageChronicle
        contacts={filteredContacts}
        timeZone={timeZone}
        contactDrawerLoading={contactDrawerLoading}
        handleOpenContactDrawer={handleOpenContactDrawer}
      />
    </Stack>
  );
};

export default MessageHistoryPanel;
